/**
 * RevenueStreamDonut — a donut chart of net revenue by stream (Rooms / Dining /
 * …) for the Accounts Overview, with the period's total revenue shown in the
 * centre and an accessible side legend giving each stream's share.
 *
 * Streams are whatever the real AVOS invoice/stay data produces (no fabricated
 * categories); additional streams appear dynamically. Money arrives as
 * integer-minor STRINGS; converted to major units (÷100) only for display.
 * Client-only (console bundle, never SSR).
 */
import type { ReactElement } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";

/** Brand-derived series palette (emerald family + one gold accent). */
const SERIES_COLORS = [
  "#1D35A9", // Primary blue
  "#0067B1", // Deep blue
  "#12B76A", // Green
  "#667085", // Slate gray
  "#F79009", // Amber
  "#84CAFF", // Light blue
];

/** One stream row (already humanised label + net revenue minor STRING). */
export interface StreamDonutRow {
  readonly label: string;
  readonly netMinor: string;
}

interface StreamDatum {
  readonly name: string;
  readonly value: number;
  readonly netMinor: string;
  readonly share: number;
}

function formatFull(minor: number): string {
  const major = minor / 100;
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(major);
  } catch {
    return `₹${major.toFixed(0)}`;
  }
}

function StreamTooltip({
  active,
  payload,
}: {
  readonly active?: boolean;
  readonly payload?: readonly { readonly payload?: StreamDatum }[];
}): ReactElement | null {
  if (active !== true || payload === undefined || payload.length === 0) {
    return null;
  }
  const d = payload[0]?.payload;
  if (d === undefined) {
    return null;
  }
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__title">{d.name}</p>
      <p className="chart-tooltip__row">
        {formatFull(Number(d.netMinor))} · {(d.share * 100).toFixed(0)}%
      </p>
    </div>
  );
}

/**
 * The revenue-by-stream donut. `totalLabel` is the pre-formatted period total
 * shown in the centre. Renders an accessible empty state when there is no
 * stream revenue in the period.
 */
export function RevenueStreamDonut({
  rows,
  totalLabel,
}: {
  readonly rows: readonly StreamDonutRow[];
  readonly totalLabel: string;
}): ReactElement {
  const total = rows.reduce((sum, r) => sum + Number(r.netMinor), 0);
  const data: StreamDatum[] = rows
    .filter((r) => Number(r.netMinor) > 0)
    .map((r) => ({
      name: r.label,
      value: Number(r.netMinor) / 100,
      netMinor: r.netMinor,
      share: total > 0 ? Number(r.netMinor) / total : 0,
    }));

  if (data.length === 0 || total <= 0) {
    return (
      <p className="console-empty">No revenue recorded for this period.</p>
    );
  }

  return (
    <div className="chart-donut">
      <div className="chart-donut__chart accounts-donut" style={{ height: 200, width: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={62}
              outerRadius={92}
              paddingAngle={1.5}
              stroke="#FFFFFF"
              strokeWidth={2}
            >
              {data.map((entry, i) => (
                <Cell
                  key={entry.name}
                  fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<StreamTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="accounts-donut__center" aria-hidden="true">
          <span className="accounts-donut__label">Total revenue</span>
          <span className="accounts-donut__value">{totalLabel}</span>
        </div>
      </div>
      <ul className="chart-legend" aria-label="Revenue by stream legend">
        {data.map((d, i) => (
          <li key={d.name} className="chart-legend__item">
            <span
              className="chart-legend__swatch"
              style={{ background: SERIES_COLORS[i % SERIES_COLORS.length] }}
              aria-hidden="true"
            />
            <span className="chart-legend__label">{d.name}</span>
            <span className="chart-legend__value">
              {(d.share * 100).toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default RevenueStreamDonut;
